/**
 * Wire types for the DOD Monitor v2 API.
 *
 * Mirror of the v2 section of
 * `backend/src/modules/distributed-order-delivery/interfaces/dod-admin.interfaces.ts`, field for
 * field. That file is the contract; this one exists only because `baseUrl: 'src'` cannot reach
 * into `backend/`. When the backend contract moves, this file moves with it — do not "fix" a type
 * here to make a component compile.
 *
 * Two deliberate deviations, both widening:
 *  - `DodCapabilities.version` is optional here (the backend always sends it, but
 *    `DOD_FALLBACK_CAPABILITIES` in the shell renders before the probe answers).
 *  - `DodCapabilities.grafanaUrl` is optional and the backend never sets it, so the
 *    "open in Grafana" affordance stays hidden until a deployment supplies one.
 */
import { FailedOrderPayload, OrderExecutionEntry, TkdQueueState } from './dod.types';

// ── Ranges ───────────────────────────────────────────────────────────

/** Time window selector shared by every v2 route that aggregates. */
export type DodRange = '15m' | '1h' | '6h' | '24h' | '7d';

export const DOD_RANGES: readonly DodRange[] = ['15m', '1h', '6h', '24h', '7d'];

/** Range → milliseconds. Same table the backend uses, so KPI deltas line up with the axis. */
export const DOD_RANGE_MS: Readonly<Record<DodRange, number>> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '6h': 6 * 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
};

// ── Pagination ───────────────────────────────────────────────────────

export const DOD_PAGE_SIZE_DEFAULT = 50;
export const DOD_PAGE_SIZE_MAX = 200;

/** Every paginated v2 route returns this envelope. `page` is 1-based. */
export type DodPage<T> = {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
};

// ── GET /dod/capabilities ────────────────────────────────────────────

export type DodCapabilities = {
  /** Redis rollups are readable. When false, every chart renders "unknown", never a zero line. */
  timeseries: boolean;
  /** PROMETHEUS_URL is configured, so GET /dod/promql answers. */
  prometheus: boolean;
  /** PROMETHEUS_RETENTION, verbatim (e.g. '15d'). */
  promRetention?: string;
  /** The rcp-worker has published a heartbeat that has not expired. */
  worker: boolean;
  /** Backend package version. Optional here: absent from the pre-probe fallback. */
  version?: string;
  /** Base URL of a Grafana dashboard. Not currently emitted by the backend. */
  grafanaUrl?: string;
};

// ── GET /dod/timeseries ──────────────────────────────────────────────

/**
 * The four label dimensions a rollup field carries. Order is fixed — it is the field encoding.
 *
 * `dim` is the generic fourth slot: whichever single discriminator the metric carries besides the
 * RCP/TKD/command triple, so `dod_dlq_moved_total` splits by reason and `dod_drain_runs_total` by
 * result without a key layout per metric.
 */
export const DOD_TIMESERIES_LABELS = ['rcpId', 'nlk', 'command', 'dim'] as const;

export type DodTimeseriesLabelName = (typeof DOD_TIMESERIES_LABELS)[number];

/**
 * `null` only ever appears for a gauge, and only before its first observation inside the window.
 * After the first observation a gauge carries its last value forward; a counter gap-fills with 0.
 */
export type DodTimeseriesPoint = [number, number | null];

export type DodTimeseriesSeries = {
  /** Group-by values joined by '|', or 'total' when the query did not group. */
  key: string;
  labels: Record<string, string>;
  points: DodTimeseriesPoint[];
};

/** How the backend filled the gaps, so a chart can label itself honestly. */
export type DodTimeseriesKind = 'counter' | 'gauge';

export type DodTimeseriesResponse = {
  series: DodTimeseriesSeries[];
  /** Resolution actually served, in milliseconds. The backend may coarsen a wide window. */
  step: number;
  kind: DodTimeseriesKind;
  metric: string;
  from: number;
  to: number;
  /** false when the rollup provider is absent — `series` is then empty and means "unknown". */
  capability: boolean;
  /** Every metric the provider can serve; the UI builds its metric picker from this. */
  availableMetrics: string[];
};

/**
 * Argument shape for the timeseries fetcher.
 *
 * The backend's nested `filter` object is flattened, because shared-ui's `useQuery` serialises the
 * whole args object with `queryString.stringify` and a nested object would not survive. `groupBy`
 * stays an array: the same serialiser runs with `arrayFormat: 'comma'`, which is exactly the
 * comma-separated list the route parses, so the type can name the four legal dimensions instead
 * of accepting any string. An unknown groupBy or step is a 400, not an empty chart.
 */
export type DodTimeseriesArgs = {
  metric: string;
  from: number;
  to: number;
  /** Step in milliseconds; one of the three rollup resolutions. Omit to let the backend pick. */
  step?: number;
  rcpId?: string;
  nlk?: string;
  command?: string;
  /** Filter on the generic fourth label, e.g. `dim: 'permanent'` on dod_dlq_moved_total. */
  dim?: string;
  /** Omit for a single total series. */
  groupBy?: DodTimeseriesLabelName[];
};

/** Counter rollups the overview KPIs and charts read. Same names Prometheus scrapes. */
export const DOD_OVERVIEW_METRICS = {
  enqueued: 'dod_orders_enqueued_total',
  delivered: 'dod_orders_delivered_total',
  failed: 'dod_orders_failed_total',
  deferred: 'dod_drain_deferred_total',
  queueDepth: 'dod_queue_depth',
  dlqSize: 'dod_dlq_size',
  dlqMoved: 'dod_dlq_moved_total',
  outboxOrders: 'dod_outbox_orders',
} as const;

// ── GET /dod/overview ────────────────────────────────────────────────

/**
 * A KPI tile value. `value` is null (not 0) when the source is unavailable — an unregistered
 * rollup provider must render as "unknown", never as "nothing happened".
 */
export type DodKpi = {
  value: number | null;
  /** Same quantity over the immediately preceding window of equal length; null when unknown. */
  previous: number | null;
  /** Percent change, one decimal; null when either side is unknown or `previous` is 0. */
  deltaPct: number | null;
};

export type DodOutboxHealthRow = {
  status: string;
  orders: number;
  oldestOrderAt: string | null;
  oldestAgeSeconds: number | null;
  maxAttempts: number | null;
};

export type DodRcpConnectionSummary = {
  rcpId: string;
  rcpName?: string;
  /** Free-form on the wire ('connected' | 'disconnected' | 'reconnecting' | 'error' | …). */
  state: string;
  circuit?: string;
  consecutiveFailures?: number;
  heartbeatAgeMs?: number;
  certExpiresAt?: string;
  reconnects?: number;
};

export type DodAlertId = 'noSessionFor5m' | 'workerDown' | 'dlqGrowing';

export type DodAlertSeverity = 'critical' | 'warning';

/**
 * An alert evaluated in the backend from the same inputs the PrometheusRule uses. `firing: false`
 * is the green state — there is no 'ok' severity. `summary` is human text, not an i18n key.
 */
/** Text variant the backend selected; translated by `dod.overview.alert.<code>` with `params`. */
export type DodAlertCode =
  | 'workerDown.noHeartbeat'
  | 'workerDown.stale'
  | 'workerDown.fresh'
  | 'noSessionFor5m.stale'
  | 'noSessionFor5m.none'
  | 'noSessionFor5m.allUp'
  | 'dlqGrowing.unknown'
  | 'dlqGrowing.growing'
  | 'dlqGrowing.stable';

export type DodAlert = {
  id: DodAlertId;
  severity: DodAlertSeverity;
  firing: boolean;
  /** Optional only for heartbeats from a backend older than the code field; the UI then shows `summary`. */
  code?: DodAlertCode;
  params?: Record<string, string | number>;
  summary: string;
  labels?: Record<string, string>;
};

export type DodOverviewResponse = {
  range: DodRange;
  /** Server clock at evaluation time (ms epoch); the UI aligns its axis to this. */
  now: number;
  queuedTotal: number;
  dlqTotal: number;
  deliveredPerMin: DodKpi;
  failedPerMin: DodKpi;
  deferredPerMin: DodKpi;
  /** Age in seconds of the oldest live outbox row; null when empty or unavailable. */
  oldestOutboxAgeSeconds: number | null;
  outbox: DodOutboxHealthRow[];
  rcp: {
    total: number;
    connected: number;
    connections: DodRcpConnectionSummary[];
  };
  lastDrainAt: string | null;
  alerts: DodAlert[];
};

export type DodOverviewArgs = {
  range: DodRange;
};

// ── Shared state vocabularies ────────────────────────────────────────

export type DodQueueStatus = 'idle' | 'draining' | 'paused';
export type DodCircuitState = 'closed' | 'open' | 'half_open';

// ── GET /dod/queues ──────────────────────────────────────────────────

export type DodQueueSort = 'depth' | 'dlq' | 'lastDrainAt';

export type DodSortOrder = 'asc' | 'desc';

export const DOD_QUEUE_SORTS: readonly DodQueueSort[] = ['depth', 'dlq', 'lastDrainAt'];

/** v2 row: v1's `DodQueueSummary` plus the columns the new table shows. */
export type DodQueueSummaryV2 = {
  rcpId: string;
  nlk: number;
  rcpNazwa?: string;
  tkdNazwa?: string;
  status: DodQueueStatus;
  tkdStatus?: string;
  depth: number;
  dlqDepth: number;
  lastDrainAt?: string;
  lastError?: string;
  /** Highest retryCount currently in the queue (not the DLQ); 0 when the queue is empty. */
  retryCountMax: number;
  /** Last 15 one-minute depth samples, oldest first. Empty when the rollup provider is absent. */
  sparkline: number[];
};

export type DodQueuesResponse = DodPage<DodQueueSummaryV2>;

/** Every query parameter GET /dod/queues accepts. All server-side; nothing needs client filtering. */
export type DodQueuesArgs = {
  rcpId?: string;
  nlk?: number;
  status?: DodQueueStatus;
  /** Case-insensitive substring over rcpNazwa, tkdNazwa, the NLK as text, the server id and lastError. */
  search?: string;
  /** Keeps only queues at or above this depth; `minDepth: 1` is "backlog only". */
  minDepth?: number;
  sort?: DodQueueSort;
  order?: DodSortOrder;
  page?: number;
  pageSize?: number;
};

// ── GET /dod/queue/:rcpId/:nlk ───────────────────────────────────────

export type QueueDetailResponseV2 = {
  rcpId: string;
  nlk: number;
  rcpNazwa?: string;
  tkdNazwa?: string;
  /** Reader device status (OK / AWARIA / NOWY …); absent on a backend that does not send it. */
  tkdStatus?: string;
  state: TkdQueueState;
  orders: DodPage<FailedOrderPayload>;
  dlqCount: number;
};

export type DodQueueDetailArgs = {
  rcpId: string;
  nlk: number;
  page?: number;
  pageSize?: number;
};

// ── Queue actions ────────────────────────────────────────────────────

export type DodQueueActionResponse = {
  rcpId: string;
  nlk: number;
  status: DodQueueStatus;
  /** drain: the Bull job id that was enqueued. Absent for pause/resume. */
  jobId?: string;
};

export type DodQueueActionArgs = {
  rcpId: string;
  nlk: number;
};

// ── Dead letter ──────────────────────────────────────────────────────

/**
 *  - `permanent`   the controller rejected the request's content, never retried further
 *  - `max-retries` the order used its whole retry budget
 *  - `unknown-cap` an UNKNOWN-category error hit the smaller unknown-retries cap
 */
export type DodDlqReason = 'permanent' | 'max-retries' | 'unknown-cap';

export const DOD_DLQ_REASONS: readonly DodDlqReason[] = ['permanent', 'max-retries', 'unknown-cap'];

export type DodErrorClass = 'PERMANENT' | 'TRANSIENT' | 'OPERATION' | 'UNKNOWN';

export type DodDlqEntry = FailedOrderPayload & {
  rcpNazwa?: string;
  tkdNazwa?: string;
  reason: DodDlqReason;
  errorClass: DodErrorClass;
  errorCode?: number;
  errorName?: string;
};

export type DodDlqResponse = DodPage<DodDlqEntry>;

export type DodDlqFilter = {
  rcpId?: string;
  nlk?: number;
  command?: number;
  reason?: DodDlqReason;
  errorClass?: DodErrorClass;
  from?: number;
  to?: number;
  /** Case-insensitive substring over lastError, order id and KAD id. */
  search?: string;
};

export type DodDlqArgs = DodDlqFilter & {
  page?: number;
  pageSize?: number;
};

export type DodDlqSummaryResponse = {
  range: DodRange;
  total: number;
  byReason: { reason: DodDlqReason; count: number }[];
  byErrorClass: { errorClass: DodErrorClass; count: number }[];
  topErrors: { message: string; count: number }[];
};

export type DodDlqSummaryArgs = {
  range: DodRange;
  rcpId?: string;
  nlk?: number;
};

/** Body of POST /dod/dlq/retry and DELETE /dod/dlq/purge. Exactly one of ids/filter is required. */
export type DodDlqBulkRequest = {
  ids?: string[];
  filter?: DodDlqFilter;
};

export type DodDlqRetryResponse = {
  retried: number;
  queues: { rcpId: string; nlk: number; retried: number }[];
};

export type DodDlqPurgeResponse = {
  purged: number;
  queues: { rcpId: string; nlk: number; purged: number }[];
};

// ── GET /dod/history ─────────────────────────────────────────────────

export type DodHistoryFilter = {
  rcpId?: string;
  nlk?: number;
  command?: string;
  status?: 'OK' | 'ERROR';
  source?: 'direct' | 'dod';
  from?: number;
  to?: number;
  search?: string;
};

export type DodHistoryEntry = OrderExecutionEntry & {
  rcpNazwa?: string;
  tkdNazwa?: string;
};

/** One reader of the fleet: the vocabulary of the history tab's RCP and TKD selects. */
export type DodHistoryReader = {
  rcpId: string;
  rcpNazwa?: string;
  nlk: number;
  tkdNazwa?: string;
};

export type DodHistoryResponse = DodPage<DodHistoryEntry> & {
  /** Every reader in the system, independent of the filters and of the page. */
  readers?: DodHistoryReader[];
};

export type DodHistoryArgs = DodHistoryFilter & {
  page?: number;
  pageSize?: number;
  /** 'csv' asks for the export instead of the JSON page. */
  format?: 'csv';
};

export const DOD_HISTORY_CSV_MAX_ROWS = 10_000;

// ── GET /dod/worker ──────────────────────────────────────────────────

export type WorkerConnectionStatus = {
  rcpId: string;
  rcpName: string;
  state: string;
  host: string | null;
  port: number | null;
  circuit: DodCircuitState;
  consecutiveFailures: number;
  lastActivityAt: string | null;
  heartbeatAgeMs: number | null;
  certExpiresAt: string | null;
  certDaysLeft: number | null;
  reconnects: { success: number; failure: number };
  connectedSince: string | null;
  lastError: string | null;
  /** Negotiated TLS protocol, e.g. 'TLSv1.2'; null before the first handshake. */
  tlsVersion: string | null;
  /** How long the last handshake took, in milliseconds; null before the first handshake. */
  handshakeMs: number | null;
  /** Common name on the peer certificate; null before the first handshake. */
  certSubjectCn: string | null;
};

export type WorkerBullQueueCounts = {
  waiting: number;
  active: number;
  delayed: number;
  failed: number;
};

export type WorkerDbPool = {
  used: number | null;
  max: number | null;
};

/**
 * The tuning the worker is actually running with, so the UI can show "limiter 20/s, sweep 120 s"
 * without the operator reading a values file. Read from live config, not from chart defaults.
 */
export type WorkerProcessConfig = {
  /** Bull rate limiter on the rcp-server-order queue; null when unset or the queue is unreachable. */
  limiterMax: number | null;
  limiterDurationMs: number | null;
  /** Resolved dod-sweep period. null means the sweep is DISABLED, not that it is unknown. */
  sweepIntervalMs: number | null;
  minRetryIntervalMs: number;
  sweepMaxDrains: number;
  maxRetries: number;
  unknownMaxRetries: number;
  /**
   * false when the process opens a connection per order instead of holding mTLS sessions, so an
   * empty `connections` list is expected. Optional: heartbeats from older workers do not carry it.
   */
  persistentTls?: boolean;
};

export type WorkerStatus = {
  podName: string;
  version: string;
  startedAt: string;
  publishedAt: string;
  eventLoopLagMs: number;
  memoryRssBytes: number;
  dbPool: WorkerDbPool;
  bull: Record<string, WorkerBullQueueCounts>;
  config: WorkerProcessConfig;
  connections: WorkerConnectionStatus[];
};

export type WorkerConnectionEvent = {
  at: string;
  rcpId: string;
  rcpName: string;
  /** The RCP_CONNECTION_EVENTS value that produced this entry. */
  event: string;
  detail: string | null;
};

/**
 * Never 404s. A missing heartbeat is `{ status: null, ageMs: null, events: [] }` so the page can
 * render "worker unknown" instead of an error toast.
 */
export type DodWorkerResponse = {
  status: WorkerStatus | null;
  ageMs: number | null;
  events: WorkerConnectionEvent[];
};

export type DodWorkerArgs = {
  /** How many connection events to return. */
  limit?: number;
};

// ── GET /dod/promql ──────────────────────────────────────────────────

export type DodPromqlArgs = {
  query: string;
  /** Seconds since epoch, as Prometheus expects. */
  start: number;
  end: number;
  /** Step in seconds. */
  step: number;
};

/** Prometheus `query_range` result, forwarded verbatim. */
export type DodPromqlResponse = {
  status: string;
  data?: {
    resultType: string;
    result: { metric: Record<string, string>; values: [number, string][] }[];
  };
  error?: string;
  errorType?: string;
};

// ── WebSocket (v2 additions) ─────────────────────────────────────────

export type DodWorkerStatusEvent = DodWorkerResponse;

export type DodOverviewTickEvent = {
  now: number;
  queuedTotal: number;
  dlqTotal: number;
  connectedRcps: number;
  totalRcps: number;
  firingAlerts: DodAlertId[];
};
